/**
 * HeroImageSectionFront.jsx
 * Created by Destin Frasier on 08/13/18.
 * Updated on 05/22/19
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Heading from './../sharedComponents/Heading';
import Button from './../sharedComponents/Button';
import Select from './../sharedComponents/Select';

export default class HeroImageSectionFront extends React.PureComponent {
    static propTypes = {
        /** Title of the page/hero image */
        title: PropTypes.string,

        /** Text that appears under the hero title */
        text: PropTypes.string,

        /** If true, the header and subhead will be black */
        hasDarkText: PropTypes.bool,

        /** If true, an underline will appear after the header */
        hasUnderline: PropTypes.bool,

        /** If true, the underline under the header will be dark green */
        hasDarkUnderline: PropTypes.bool,

        /** If true, a button will be added to the hero section */
        hasButton: PropTypes.bool,

        /** If true, the button will automatically scroll to a specific anchor on the page.  */
        isScroll: PropTypes.bool,

        /** Sets the style/appearance for the button. */
        buttonAppearance: PropTypes.oneOf([
            'primary',
            'secondary',
            'tertiary',
            'tertiary-alt',
            'tertiary-white',
            'solid-white',
            'subtle',
            'link',
            'danger',
            'subtle-danger',
            'pill'
        ]),

        /** Text that appears within the button */
        buttonText: PropTypes.string,

        /** URL or ID for a specific place on the same page. (If this is a ID, make sure that "isScroll" is set to true)  */
        buttonLink: PropTypes.string,

        /** Added to existing classes */
        className: PropTypes.string,

        /** Set the landmark or html tag for the component */
        semanticElementName: PropTypes.oneOf(['nav', 'main', 'section', 'div']),

        /** If true, a select dropdown and button will be added to this component.  */
        hasSelectElem: PropTypes.bool,

        /** Set an ID for the select component */
        selectID: PropTypes.string,

        /** Set a label for the select component */
        selectLabel: PropTypes.string,

        /** Set a render prop method to make the dev able to add a custom component */
        render: PropTypes.func,

        /** Set placeholder text the select component */
        selectPlaceholder: PropTypes.string,

        /** Set the button text for the select component */
        selectButtonText: PropTypes.string,

        /** Set the button onClick handler for the select component */
        selectButtonOnClick: PropTypes.func,

        /** Set the dropdown menu options for the select component */
        selectOptions: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string
        }))
    };

    static defaultProps = {
        hasButton: false,
        isScroll: false,
        text: '',
        semanticElementName: 'div',
        hasSelectElem: false,
        hasDarkText: false,
        hasUnderline: true,
        hasDarkUnderline: false,
        buttonLink: '#'
    };

    handleSelectButtonClick = () => {
        const selectOptionsIndex = this.selectRef.selectedIndex - 1;

        if (selectOptionsIndex >= 0) {
            this.props.selectButtonOnClick(this.props.selectOptions[selectOptionsIndex]);
        }
    }

    handleScrollClick = (event) => {
        event.preventDefault();

        const { buttonLink } = this.props;
        document.getElementById(buttonLink).scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }

    render() {
        const {
            className,
            hasDarkText,
            title,
            text,
            hasDarkUnderline,
            hasUnderline,
            semanticElementName,
            hasButton,
            isScroll,
            buttonText,
            buttonLink,
            buttonAppearance,
            hasSelectElem,
            selectID,
            selectLabel,
            selectPlaceholder,
            selectOptions,
            render,
            selectButtonText
        } = this.props;

        const SemanticElementName = semanticElementName;

        return (
            <SemanticElementName
                role="img"
                aria-label={title}
                className={cx('rec-section-hero-full-width-wrap', className, {
                    'no-background-gradient': hasDarkText
                })}>
                <div className="rec-section-hero-body-outer-wrap">
                    <div className="rec-section-hero-body">
                        <Heading
                            headingLevel={1}
                            appearance="h1"
                            className={cx('rec-section-hero-title', {
                                'rec-long-hero-title': title.length > 30,
                                'has-dark-text': hasDarkText,
                                'has-dark-underline': hasDarkUnderline,
                                'has-no-underline': !hasUnderline
                            })}>
                            {title}
                        </Heading>

                        {!!text && (
                            <p
                                className={cx('rec-section-hero-text', {
                                    'has-dark-text': hasDarkText
                                })}>
                                {text}
                            </p>
                        )}

                        {hasSelectElem && (
                            <div className="rec-section-hero-select-wrap">
                                <Select
                                    id={selectID}
                                    label={selectLabel}
                                    isLabelVisible={false}
                                    placeholder={selectPlaceholder}
                                    options={selectOptions}
                                    innerRef={(ref) => this.selectRef = ref}
                                    isInline />
                                <Button
                                    appearance={buttonAppearance}
                                    size="lg"
                                    className="hero-select-button"
                                    onClick={this.handleSelectButtonClick}>
                                    {selectButtonText}
                                </Button>
                            </div>
                        )}

                        {hasButton && (
                            <Button
                                appearance={buttonAppearance}
                                href={isScroll ? `#${buttonLink}` : buttonLink}
                                size="lg"
                                className="mt-2"
                                onClick={isScroll ? this.handleScrollClick : null}>
                                {buttonText}
                            </Button>
                        )}
                        {render && render()}
                    </div>
                </div>
            </SemanticElementName>
        );
    }
}

